import 'utils/setupApp';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';

import BravelyData from 'utils/data';

import FormRenderer from 'form-renderer';
import { reducer } from 'form-renderer/shared/reducer';
import { StateProvider } from 'form-renderer/shared/state';
import { getQuestionsTotal } from 'form-renderer/shared/lib';
import { initializeForm } from 'form-renderer/shared/data';

import styles from './styles';

// FormRenderer -- Local Feature Flags
const DEFAULT_FEATURES = {
  autoSave: true,
  progressMeter: true,
  categories: true,
  numberedQuestions: true,
  optionalLabel: true,
  editable: true,
  showEmptyAnswers: false,
};

// FormRenderer -- Initial State
const initialState = ({ submission, context }) => {
  const {
    questions,
    conditions,
    hidden,
    required,
    initialValues,
  } = initializeForm({ submission, context });

  return {
    submission: {
      id: submission.id,
      name: submission.structure.name,
      salt: submission.salt,
    },
    questions: questions,
    conditions: conditions,
    hidden: hidden,
    required: required,
    data: submission.data, // Server-side Raw Data
    initialValues: initialValues, // Client-side Data prepared for Formik
    context: context,
    questionsTotal: getQuestionsTotal({ required, hidden }),
    questionsCompleted: 0,
    isNew: submission.isNew,
    isReadOnly:
      submission.isReadOnly ||
      context.forceReadOnly /* Some contexts we want to force read only behavior */,
    validationSchema: null,
    redirectTo: null,
    redirecting: false,
    features: {
      ...DEFAULT_FEATURES,
      ...(context.features || {}),
    },
  };
};

export const FormRendererStateProvider = ({
  ViewComponent = null,
  viewComponentProps = {},
}) => {
  // FormRenderer -- Bootstrap
  const bootstrap = BravelyData('formRenderer');

  if (!bootstrap || !bootstrap.submission) {
    throw "BravelyData is missing: 'formRenderer'";
  }

  const viewComponent = ViewComponent && (
    <ViewComponent {...viewComponentProps} />
  );

  const renderHeader = () => {
    const {
      submission: { structure },
      context: { features },
    } = bootstrap;

    return (
      features.showFormTitle && (
        <div className={`${styles.surveyHeader} ${styles.surveyContainer}`}>
          <h1 className={styles.surveyHeadline}>{structure.label}</h1>
          <h2 className={styles.surveyInfo}>{structure.description}</h2>
        </div>
      )
    );
  };

  return (
    <StateProvider initialState={initialState(bootstrap)} reducer={reducer}>
      <Router basename={bootstrap.context.basePath}>
        {renderHeader()}
        <FormRenderer viewComponent={viewComponent} />
      </Router>
    </StateProvider>
  );
};
const node = document.querySelector('#form-renderer');
if (Boolean(node)) {
  const root = createRoot(node);
  root.render(<FormRendererStateProvider />);
}
